/**
 * How to Play (task 9.9).
 *
 * The same content is a route and a mid-mission overlay, so what the player is
 * told never depends on where they asked. Deliberately about *mechanics* rather
 * than about any mission: it explains that any combination may be selected and
 * that consequences appear only on send, which is the one thing the composer
 * cannot show you without spoiling it (design.md, composer discoverability).
 */
import type { JSX } from 'react';
import { LaunchLayout, PageHeading } from '../components/LaunchLayout.js';

interface Topic {
  readonly id: string;
  readonly title: string;
  readonly body: readonly string[];
}

/**
 * Exported for `copy-tables.test.ts`: `title` is Flavour, every `body` paragraph
 * Instructional (design D4, D8).
 */
export const TOPICS: readonly Topic[] = [
  {
    id: 'assembly',
    title: 'You build the message. You do not type it.',
    body: [
      'You build every message from phrases dropped into four slots: an opener, a pivot, an optional buzzword, and the ask. The game has no text box.',
      'Your built message appears above the send control, joined exactly as it will post. What you send is what you read.',
    ],
  },
  {
    id: 'unconstrained',
    title: 'Any combination can be sent',
    body: [
      'The phrases you hold sit in your tray. Any phrase in your tray can go into any slot that fits its role. Nothing is greyed out, hidden, reordered or marked as a bad idea.',
      'Consequences appear only after you send. Reading the channel is the game. The interface will not read it for you.',
    ],
  },
  {
    id: 'harvesting',
    title: 'Collect phrases from what people say',
    body: [
      'Some words in other people’s messages are highlighted. Select a highlight to collect that phrase. It goes into your tray and works like any phrase you started with.',
      'A collected phrase keeps the meaning it had in the original message. That is not always the meaning the person intended.',
    ],
  },
  {
    id: 'targets',
    title: 'Who you address, and whose voice you use',
    body: [
      'A channel is a place people talk, either a group thread or a direct message. Each message is addressed to one person in the channel. Different people read the same message in very different ways.',
      'You also choose which voice the message uses. Your own voice is the default. Once you hold someone’s voice you can speak as them, which changes who will listen and what a mistake costs.',
    ],
  },
  {
    id: 'clock',
    title: 'The clock waits for you',
    body: [
      'Each day has three phases: Morning, Midday and Exec sync. Within a phase, time is counted in ticks. A tick passes each time you send or wait. Only you can move to the next phase.',
      'Scheduled events fire when you enter a phase, before you act. Each mission gives you a fixed number of phases. When they are gone, the mission ends.',
    ],
  },
  {
    id: 'strikes',
    title: 'HR is reading',
    body: [
      'A message that is aggressive or out of band earns an HR strike. The first strike locks your composer for a short time. The second limits you to a careful opener. Enough strikes end your job and the mission.',
      'How many strikes you get before that happens depends on the difficulty you chose.',
    ],
  },
  {
    id: 'puppets',
    title: 'Voices',
    body: [
      'Earn enough trust from a colleague and you can send messages as them. Their voice opens channels and phrases that you do not have yourself.',
      'You can hold only so many voices at once. Speaking in a voice badly costs you credibility with that person. A voice you ignore returns to acting on its own.',
    ],
  },
];

/** The body, reusable as a route and inside the overlay. */
export function HowToPlayContent(): JSX.Element {
  return (
    <div className="flex flex-col gap-6" data-testid="how-to-play-content">
      {TOPICS.map((topic) => (
        <section key={topic.id} data-testid={`how-to-play-${topic.id}`}>
          <h2 className="text-base font-semibold text-ink">{topic.title}</h2>
          {topic.body.map((paragraph) => (
            <p key={paragraph.slice(0, 24)} className="mt-2 text-sm text-ink-muted">
              {paragraph}
            </p>
          ))}
        </section>
      ))}
    </div>
  );
}

export function HowToPlayScreen(): JSX.Element {
  return (
    <LaunchLayout title="How to Play">
      <PageHeading
        title="How to Play"
        lede="The mechanics, in the order you meet them. None of this spoils a mission."
      />
      <HowToPlayContent />
    </LaunchLayout>
  );
}
